import { css } from '@emotion/react';
import {
  Button,
  Card,
  CheckBox,
  color,
  Icon,
  iconSize,
  spacing,
  Typography
} from '@uniquegood/realworld-studio-design';
import { useState, useEffect, useRef, useMemo } from 'react';
import queryString from 'query-string';
import Flatpickr from 'react-flatpickr';
import { Korean } from 'flatpickr/dist/l10n/ko';
import dayjs from 'dayjs';
import download from 'downloadjs';
import { RowFlex, JustifySpaceBetween } from '@/styles';
import { useRequest } from '@/hooks';
import PeriodSelector from './PeriodSelector';
import { PeriodOptionValue } from '@/config/planLimits';
import '@styles/flatpickr.css';
import meatballIcon from '@/pages/dashboard/assets/meatball.svg';
import { coreApi } from '@/api';
import { ClickEventName, track } from '@/track';

interface PlayTimeSectionProps {
  onPeriodChange?: (period: PeriodOptionValue) => void;
  appId?: string;
  projectId?: string;
  canDownload?: boolean;
}

const ITEMS_PER_PAGE = 10;

export default function PlayTimeSection({
  onPeriodChange,
  appId,
  projectId,
  canDownload = false
}: PlayTimeSectionProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [showCalendar, setShowCalendar] = useState(false);
  const [withUnfinished, setWithUnfinished] = useState(false);
  const calendarWrapperRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout>();

  const { data: applicationInfo, mutate: mutateApplicationInfo } = useRequest<
    CommonResponseModel<ApplicationInfoResponseModel>
  >(`/apps/${appId}/info`);
  const { data: projectDataResponse, mutate: mutateProjectData } = useRequest<
    CommonResponseModel<ProjectV2>
  >(
    `/apps/${appId}/projects/${projectId}`,
    {},
    {
      headers: {
        'x-rwd-api-version': '1.1'
      }
    }
  );
  const projectData = projectDataResponse?.data;
  const { data: me, mutate: mutateMe } = useRequest<MyInfo>('/api/me');

  // Cleanup: 타이머 정리
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // PlayTimeSection은 Enterprise/Class만 접근 가능하므로 항상 year를 기본값으로 사용
  const getDefaultPeriod = (): PeriodOptionValue => {
    return 'year';
  };

  // 기본 날짜 범위 계산 (year 기준)
  const defaultDateRange = useMemo(() => {
    const days = 365; // year 기준
    return {
      startDate: dayjs()
        .subtract(days - 1, 'day')
        .startOf('day')
        .format(),
      endDate: dayjs().endOf('day').format()
    };
  }, []);
  const defaultPeriod = useMemo(() => getDefaultPeriod(), []);

  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOptionValue>(defaultPeriod);
  const [dateRange, setDateRange] =
    useState<{ startDate: string; endDate: string }>(defaultDateRange);
  const periodQuery = queryString.stringify(dateRange);

  // API 호출 URL 생성
  const apiUrl = useMemo(() => {
    if (!projectId) return '';
    return `/apps/${appId}/projects/${projectId}/statistics/playtime?page=${currentPage}&take=${ITEMS_PER_PAGE}&withUnfinished=${withUnfinished}&${periodQuery}`;
  }, [appId, projectId, currentPage, withUnfinished, dateRange.startDate, dateRange.endDate]);

  // API 호출
  const { data: { data: playtimeData } = {}, isValidating: isLoading } =
    useRequest<CommonResponseModel<UserPlaytimeStatisticsResponseModelPaginatedResponse>>(apiUrl);

  const playtimeList = playtimeData?.data ?? [];
  const totalPages = playtimeData?.totalPages ?? 0;

  // 기간 변경 핸들러
  const handlePeriodChange = (period: PeriodOptionValue) => {
    setSelectedPeriod(period);
    setCurrentPage(1); // 페이지를 첫 페이지로 리셋
    closeCalendar();

    const { startDate: newStartDate, endDate: newEndDate } = getPeriodDates(period);
    const newDateRange = { startDate: newStartDate, endDate: newEndDate };
    setDateRange(newDateRange);

    // 부모 컴포넌트에 기간 변경 알림
    if (onPeriodChange) {
      onPeriodChange(period);
    }
  };

  // 커스텀 날짜 선택 - 캘린더 열기
  const handleDateRangeClick = () => {
    // 닫기 타이머가 있다면 취소
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setSelectedPeriod('custom');
    setShowCalendar(true);
  };

  // 캘린더 닫기 (딜레이 적용)
  const handleCalendarClose = () => {
    // 200ms 딜레이 후 닫기 (캘린더로 마우스 이동 시간 확보)
    closeTimeoutRef.current = setTimeout(() => {
      setShowCalendar(false);
    }, 200);
  };

  // 캘린더 영역에 마우스가 들어오면 닫기 취소
  const handleCalendarMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
  };

  const closeCalendar = () => {
    setShowCalendar(false);
  };

  const handleDownload = async () => {
    if (!appId || !projectId) return;

    const query = queryString.stringify(dateRange);
    const response = await coreApi.get<Blob>(
      `/apps/${appId}/projects/${projectId}/statistics/playtime/download?${query}`,
      { responseType: 'blob' }
    );

    // Content-Disposition 헤더에서 파일명 추출
    const contentDisposition = response.headers['content-disposition'];
    const fileNameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
      contentDisposition || ''
    );
    const rawFileName = fileNameMatch?.[1] || fileNameMatch?.[2];

    // 서버에서 파일명을 주면 사용, 아니면 기본값 사용
    const dates = [dateRange.startDate, dateRange.endDate].map((date) =>
      dayjs(date).format('YYYY-MM-DD')
    );
    const fileName = rawFileName
      ? decodeURIComponent(rawFileName)
      : `playtime_${projectId}_${dates.join('-')}.xlsx`;

    download(response.data, fileName);

    let applicationInfoData = applicationInfo;
    let projectDataData = projectData;
    let meInfo = me;

    if (!applicationInfo) {
      applicationInfoData = await mutateApplicationInfo();
    }

    if (!projectData) {
      const mutatedData = await mutateProjectData();
      projectDataData = mutatedData?.data;
    }

    if (!meInfo) {
      meInfo = await mutateMe();
    }

    const param = {
      channel_id: appId,
      channel_name: applicationInfoData?.data.name,
      use_purpose_type: applicationInfoData?.data.usePurposeType,
      plan_type: applicationInfoData?.data.planType,
      project_id: projectId,
      project_name: projectDataData?.name,
      user_id: meInfo?.id,
      dashboard_type: '플레이 시간'
    };

    track.onClick({
      clickEventName: ClickEventName.click_dashboard_button_download,
      params: param
    });
  };

  const formatPlayTime = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return '-';

    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}시간 ${mins}분 ${secs}초`;
    }
    if (mins > 0) {
      return `${mins}분 ${secs}초`;
    }
    return `${secs}초`;
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // 페이지 번호 생성
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];

    if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, 5, 'ellipsis-end', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(
        1,
        'ellipsis-start',
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(
        1,
        'ellipsis-start',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        'ellipsis-end',
        totalPages
      );
    }

    return pages;
  };

  return (
    <Card cssStyle={CardStyle}>
      {/* 헤더 */}
      <div css={[RowFlex, JustifySpaceBetween, HeaderStyle]}>
        <Typography as="h3" type="heading">
          플레이 시간
        </Typography>
        <Button icon="download_solid" type="plain" onClick={handleDownload} disabled={!canDownload}>
          다운로드
        </Button>
      </div>

      {/* 필터 영역 */}
      <div css={FilterWrapperStyle}>
        <div ref={calendarWrapperRef} css={CalendarWrapperStyle}>
          <PeriodSelector
            plan="Enterprise" // PlayTimeSection은 Enterprise/Class만 접근 가능하므로 모든 기간 옵션 사용
            selectedPeriod={selectedPeriod}
            onPeriodChange={handlePeriodChange}
            onDateRangeClick={handleDateRangeClick}
            dateRange={dateRange}
          />
          {showCalendar && (
            <div
              css={CalendarDropdownStyle}
              onMouseEnter={handleCalendarMouseEnter}
              onMouseLeave={handleCalendarClose}
            >
              <Flatpickr
                options={{
                  mode: 'range',
                  inline: true,
                  minDate: getMinDateByPlan('Enterprise') ?? undefined, // PlayTimeSection은 Enterprise/Class만 접근 가능
                  maxDate: 'today',
                  locale: Korean,
                  defaultDate: [dateRange.startDate, dateRange.endDate]
                }}
                value={[dateRange.startDate, dateRange.endDate]}
                onChange={([selectedStartDate, selectedEndDate]) => {
                  if (selectedStartDate && selectedEndDate) {
                    const newDateRange = {
                      startDate: dayjs(selectedStartDate).format(),
                      endDate: dayjs(selectedEndDate).format()
                    };

                    setDateRange(newDateRange);
                    setShowCalendar(false);

                    // 부모 컴포넌트에 알림
                    if (onPeriodChange) {
                      onPeriodChange('custom');
                    }
                  }
                }}
              />
            </div>
          )}
        </div>

        <CheckBox
          checked={withUnfinished}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setWithUnfinished(e.target.checked);
            setCurrentPage(1); // 필터 변경 시 첫 페이지로 리셋
          }}
        >
          미종료 유저도 보기
        </CheckBox>
      </div>

      {/* 테이블 */}
      <div css={TableContainerStyle}>
        <table css={TableStyle}>
          <thead>
            <tr>
              <th css={ProfileColumnStyle}>프로필</th>
              <th>유저 닉네임</th>
              <th>시작 시각</th>
              <th>종료 시각</th>
              <th css={PlayTimeColumnStyle}>플레이 시간</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} css={LoadingCellStyle}>
                  <div css={LoadingContentStyle}>
                    <Typography type="body" color={color.text_subdued}>
                      데이터를 불러오는 중...
                    </Typography>
                  </div>
                </td>
              </tr>
            )}
            {!isLoading && playtimeList.length === 0 && (
              <tr>
                <td colSpan={5} css={LoadingCellStyle}>
                  <div css={LoadingContentStyle}>
                    <Typography type="body" color={color.text_subdued}>
                      데이터가 없습니다
                    </Typography>
                  </div>
                </td>
              </tr>
            )}
            {!isLoading &&
              playtimeList.length > 0 &&
              playtimeList.map((user) => (
                <tr key={user.userId}>
                  <td css={ProfileCellStyle}>
                    <div css={AvatarStyle}>
                      {user.userProfileImageUrl ? (
                        <img src={user.userProfileImageUrl} alt={user.userName || 'User'} />
                      ) : (
                        <Icon
                          icon="check_solid"
                          size={iconSize.medium}
                          color={color.icon_subdued}
                        />
                      )}
                    </div>
                  </td>
                  <td>
                    <Typography type="body">{user.userName || '{유저 닉네임}'}</Typography>
                  </td>
                  <td>
                    <Typography type="body">{formatDateTime(user.startedAt)}</Typography>
                  </td>
                  <td>
                    <Typography type="body">
                      {formatDateTime(user.finishedAt || undefined)}
                    </Typography>
                  </td>
                  <td>
                    <Typography type="body" cssStyle={PlayTimeStyle(user.totalPlayTimeSeconds)}>
                      {formatPlayTime(user.totalPlayTimeSeconds)}
                    </Typography>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div css={PaginationStyle}>
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => prev - 1)}
            css={PageNavButtonStyle}
          >
            <Icon
              icon="chevron_right_regular"
              size={iconSize.small}
              color={currentPage === 1 ? color.icon_disabled : color.icon_subdued}
              css={css`
                transform: rotate(-180deg);
              `}
            />
          </button>

          {getPageNumbers().map((page) => {
            if (typeof page === 'string') {
              return (
                <div key={page} css={EllipsisStyle}>
                  <img src={meatballIcon} alt="meatball" />
                </div>
              );
            }

            return (
              <button
                key={`page-${page}`}
                type="button"
                css={[PageNumberButtonStyle, currentPage === page && PageNumberActiveStyle]}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            );
          })}

          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((prev) => prev + 1)}
            css={PageNavButtonStyle}
          >
            <Icon
              icon="chevron_right_regular"
              size={iconSize.small}
              color={currentPage === totalPages ? color.icon_disabled : color.icon_subdued}
            />
          </button>
        </div>
      )}
    </Card>
  );
}

const CardStyle = css`
  max-width: 1300px;
  min-width: 740px;
  margin-bottom: ${spacing.margin.xlarge4};
`;

const HeaderStyle = css`
  margin-bottom: ${spacing.margin.xlarge2};
  align-items: center;
`;

const FilterWrapperStyle = css`
  ${RowFlex}
  align-items: center;
  gap: ${spacing.margin.xlarge2};
  margin-bottom: ${spacing.margin.xlarge2};
`;

const CalendarWrapperStyle = css`
  position: relative;
`;

const CalendarDropdownStyle = css`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 1000;
  background: ${color.surface_default_default};
  border-radius: ${spacing.borderRadius.large};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: ${spacing.padding.medium};

  /* Flatpickr inline 모드 스타일 커스터마이징 */
  .flatpickr-calendar {
    box-shadow: none;
    background: transparent;
  }

  input {
    display: none;
  }

  /* 애니메이션 */
  animation: slideDown 200ms ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const TableContainerStyle = css`
  border: 1px solid ${color.border_default_subdued};
  border-radius: ${spacing.borderRadius.large};
  overflow: hidden;
`;

const TableStyle = css`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: ${spacing.padding.medium};
    text-align: left;
    border-bottom: 1px solid ${color.border_default_subdued};
  }

  th {
    background-color: ${color.surface_default_subdued};
    color: ${color.text_default};
    font-weight: 500;
    font-size: 14px;
  }

  tbody tr:hover {
    background-color: ${color.surface_default_hovered};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const ProfileColumnStyle = css`
  width: 69px;
`;

const PlayTimeColumnStyle = css`
  width: 140px;
`;

const ProfileCellStyle = css`
  padding: ${spacing.padding.small} ${spacing.padding.medium} !important;
`;

const AvatarStyle = css`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  background-color: ${color.surface_default_subdued};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PlayTimeStyle = (playTime: number | null | undefined) => css`
  color: ${playTime === null || playTime === undefined ? color.text_disabled : color.text_default};
`;

const PaginationStyle = css`
  ${RowFlex}
  justify-content: center;
  align-items: center;
  gap: ${spacing.margin.medium};
  margin-top: ${spacing.margin.xlarge2};
  padding: ${spacing.padding.xsmall} ${spacing.padding.small};
`;

const PageNavButtonStyle = css`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: ${spacing.padding.xsmall};
  border-radius: ${spacing.borderRadius.small};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  &:hover:not(:disabled) {
    background-color: ${color.surface_default_hovered};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const PageNumberButtonStyle = css`
  border: none;
  background: transparent;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: ${color.text_subdued};
  border-radius: ${spacing.borderRadius.small};

  &:hover {
    background-color: ${color.surface_default_hovered};
  }
`;

const PageNumberActiveStyle = css`
  background-color: ${color.surface_default_subdued};
  color: ${color.text_default};
`;

const EllipsisStyle = css`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingCellStyle = css`
  text-align: center;
  padding: ${spacing.padding.xlarge3} !important;
  border-bottom: none !important;
`;

const LoadingContentStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.margin.medium};
  min-height: 200px;
`;

// Helper functions
function getPeriodDates(period: PeriodOptionValue) {
  const today = dayjs();

  if (period === 'today') {
    return {
      startDate: today.startOf('day').format(),
      endDate: today.endOf('day').format()
    };
  }

  if (period === 'yesterday') {
    const yesterday = today.subtract(1, 'day');
    return {
      startDate: yesterday.startOf('day').format(),
      endDate: yesterday.endOf('day').format()
    };
  }

  const periodMap: Record<string, number> = {
    week: 6,
    month: 29,
    '3months': 89,
    '6months': 179,
    year: 364
  };

  const days = periodMap[period] || 6;

  return {
    startDate: today.subtract(days, 'day').startOf('day').format(),
    endDate: today.endOf('day').format()
  };
}

function getMinDateByPlan(plan: PlanType) {
  if (plan === 'Free') {
    return dayjs().subtract(7, 'day').toDate();
  }
  if (plan === 'Pro' || plan === 'Teacher') {
    return dayjs().subtract(30, 'day').toDate();
  }

  if (plan === 'Enterprise' || plan === 'Class') {
    return dayjs().subtract(365, 'day').toDate();
  }

  return null;
}
